/*
Copyright (c) 2018 Ady Levy

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import compose from 'recompose/compose'
import {withStyles} from '@material-ui/core/styles'
import {alpha} from '@material-ui/core/styles/colorManipulator'
import ActionDelete from '@material-ui/icons/Delete'
import classnames from 'classnames'
import {translate, crudDelete, startUndoable} from 'ra-core'
import IconCancel from '@material-ui/icons/Cancel'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import {Button, usePermissions} from 'react-admin'
import {hasEditPermission} from '../../app-configs/auth/utils'

const styles = theme => ({
    deleteButton: {
        color: theme.palette.error.main,
        '&:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.12),
            // Reset on mouse devices
            '@media (hover: none)': {
                backgroundColor: 'transparent'
            }
        }
    }
})

const CustomDeleteBtnConfirmation = props => {
    const [showDialog, setDialog] = useState(false)
    const {permissions} = usePermissions()
    // fixme close the Aside / reset  SelectedItem in grid
    const handleDelete = event => {
        event.preventDefault()
        setDialog(false)
        const {
            dispatchCrudDelete,
            startUndoable,
            resource,
            record,
            basePath,
            redirect,
            undoable,
            funcClose
        } = props
        if (undoable) {
            startUndoable(
                crudDelete(resource, record.id, record, basePath, redirect)
            )
        } else {
            dispatchCrudDelete(resource, record.id, record, basePath, redirect)
        }

        if (funcClose) {
            funcClose()
        }
    }

    const {label = 'ra.action.delete', classes = {}, className} = props

    return hasEditPermission(permissions) ? (
        <>
            <Button
                key="button"
                label={label}
                className={classnames(
                    'ra-delete-button',
                    classes.deleteButton,
                    className
                )}
                onClick={() => setDialog(true)}
            >
                <ActionDelete/>
            </Button>
            <Dialog
                fullWidth
                open={showDialog}
                aria-label="Are you sure?"
                onClose={() => setDialog(false)}
            >
                <DialogTitle>
                    Are you sure you want to delete this Variation?
                </DialogTitle>
                <DialogContent/>
                <DialogActions>
                    <Button
                        key="button"
                        label="Delete Variation"
                        className={classnames(
                            'ra-delete-button',
                            classes.deleteButton,
                            className
                        )}
                        onClick={event => handleDelete(event)}
                    >
                        <ActionDelete/>
                    </Button>
                    <Button
                        label="ra.action.cancel"
                        onClick={() => setDialog(false)}
                    >
                        <IconCancel/>
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    ) : null
}

CustomDeleteBtnConfirmation.propTypes = {
    basePath: PropTypes.string,
    classes: PropTypes.object,
    className: PropTypes.string,
    dispatchCrudDelete: PropTypes.func.isRequired,
    label: PropTypes.string,
    record: PropTypes.object,
    redirect: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.func
    ]),
    resource: PropTypes.string.isRequired,
    startUndoable: PropTypes.func,
    undoable: PropTypes.bool,
    funcClose: PropTypes.func
}

CustomDeleteBtnConfirmation.defaultProps = {
    redirect: 'list',
    undoable: true,
    funcClose: undefined,
    basePath: '',
    classes: {},
    className: '',
    label: '',
    record: {},
    startUndoable: () => {}
}

export default compose(
    connect(null, {startUndoable, dispatchCrudDelete: crudDelete}),
    translate,
    withStyles(styles)
)(CustomDeleteBtnConfirmation)
